/**
 * PWA INSTALL PROMPT
 * ===================
 * Prompt customizado para instalação do app na tela inicial
 */

import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone, Zap } from 'lucide-react';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Verifica se já está instalado
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Captura o evento beforeinstallprompt
    const handler = (e: Event) => {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      setDeferredPrompt(promptEvent);

      // Mostra o prompt depois de 3 segundos
      setTimeout(() => {
        // Verifica se usuário já recusou antes
        const dismissed = localStorage.getItem('pwa-install-dismissed');
        if (!dismissed) {
          setShowPrompt(true);
        }
      }, 3000);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Detecta quando o app é instalado
    window.addEventListener('appinstalled', () => {
      console.log('PWA installed successfully!');
      setIsInstalled(true);
      setShowPrompt(false);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    // Mostra o prompt nativo de instalação
    deferredPrompt.prompt();

    // Aguarda escolha do usuário
    const { outcome } = await deferredPrompt.userChoice;

    console.log(`User response to install prompt: ${outcome}`);

    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    } else {
      console.log('User dismissed the install prompt');
      // Salva que o usuário recusou (não mostrar por 7 dias)
      localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    }

    // Limpa o prompt
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Salva que o usuário fechou (não mostrar por 7 dias)
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  // Não mostra se já instalado ou se não deve mostrar
  if (isInstalled || !showPrompt) {
    return null;
  }

  return (
    <div className='fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-50 animate-in slide-in-from-bottom-5'>
      <Card className='border-2 border-blue-500 shadow-2xl'>
        <CardContent className='p-4'>
          {/* Close button */}
          <button
            onClick={handleDismiss}
            className='absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors'
            aria-label='Fechar'
          >
            <X className='w-5 h-5' />
          </button>

          {/* Content */}
          <div className='flex gap-4'>
            {/* Icon */}
            <div className='flex-shrink-0'>
              <div className='w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center'>
                <Smartphone className='w-8 h-8 text-white' />
              </div>
            </div>

            {/* Text */}
            <div className='flex-1'>
              <h3 className='font-bold text-lg mb-1'>Instalar App</h3>
              <p className='text-sm text-gray-600 mb-3'>
                Adicione à sua tela inicial para acesso rápido e recursos offline
              </p>

              {/* Benefits */}
              <div className='space-y-1 mb-4'>
                <div className='flex items-center gap-2 text-xs text-gray-700'>
                  <Zap className='w-3 h-3 text-yellow-500' />
                  <span>Acesso instantâneo</span>
                </div>
                <div className='flex items-center gap-2 text-xs text-gray-700'>
                  <Zap className='w-3 h-3 text-green-500' />
                  <span>Funciona offline</span>
                </div>
                <div className='flex items-center gap-2 text-xs text-gray-700'>
                  <Zap className='w-3 h-3 text-blue-500' />
                  <span>Notificações em tempo real</span>
                </div>
              </div>

              {/* Actions */}
              <div className='flex gap-2'>
                <Button
                  onClick={handleInstallClick}
                  className='flex-1 bg-blue-600 hover:bg-blue-700'
                  size='sm'
                >
                  <Download className='w-4 h-4 mr-2' />
                  Instalar
                </Button>
                <Button onClick={handleDismiss} variant='outline' size='sm'>
                  Agora não
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Hook para detectar se app está instalado
 */
export function useIsInstalled() {
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    const checkInstalled = () => {
      // Verifica se está rodando em standalone mode
      const standalone = window.matchMedia('(display-mode: standalone)').matches;

      // Verifica se está no iOS standalone
      const ios = (window.navigator as any).standalone === true;

      setIsInstalled(standalone || ios);
    };

    checkInstalled();

    // Listener para mudanças
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    mediaQuery.addEventListener('change', checkInstalled);

    return () => {
      mediaQuery.removeEventListener('change', checkInstalled);
    };
  }, []);

  return isInstalled;
}
